import {Grid, MenuItem, Select, styled, TextField, Typography} from "@mui/material";
import React, {useEffect} from "react";
import MWCaption from "../../components/MWCaption";
import {FormProvider, SubmitHandler, Controller, useForm} from "react-hook-form";

import PersonIcon from "@mui/icons-material/Person";
import {Box} from "@mui/system";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {useRecoilSnapshot, useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {useDepartmentList} from "../../hooks/useDepartmentList";
import {EQUser, initializeUser} from "../../models/serverModels/EQUser";
import {tokenSelector} from "../../Recoil/Atom/authState";
import {useQueryClient} from "react-query";
import {useNavigate, useParams} from "react-router-dom";
import {useGetQuery, useUpdateMutation} from "../../hooks/useQueryHooks";
import {snackBarState, snackBarToggleState} from "../../Recoil/Atom/snackBarState";
import {ResponseItem} from "../../models/clientModels/interfaces/Response";
import * as userApi from "../../api/userApi/userApi";
import * as Params from "../../models/clientModels/interfaces/apiParams";
import {FormSection} from "../../styledComponents/StyledCreateForm";
import SubTitle from "../../components/SubTitle";
import MWProgress from "../../components/MWProgress";
import {userState} from "../../Recoil/Atom/authState";
import ConfirmButtonGroup from "../../components/ConfirmButtonGroup";

const CategoryFormContainer = styled(Grid)(({theme}) => ({
  justifyContent: "center",
  alignItems: "center",
  marginTop: "16px",

  backgroundColor: "#fff",
  "& p": {
    marginTop: "8px",
    textAlign: "center",
  },
}));
const CategoryFormContent = styled(Grid)(({theme}) => ({
  display: "flex",
  flexFlow: "column",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  margin: "32px 0",

  padding: "24px",
  border: `1px solid ${theme.palette.background.paper}`,
  borderRadius: "16px",
  boxShadow:
    "rgb(0 0 0 / 10%) 0rem 0.25rem 0.375rem -0.0625rem, rgb(0 0 0 / 6%) 0rem 0.125rem 0.25rem -0.0625rem",

  "& label": {
    fontWeight: 600,
    paddingRight: "8px",
  },
}));

const DocumentList = styled(Box)(({theme}) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  "& label": {
    flexBasis: "30%",
  },
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
}));

interface confirm {
  loginPWConfirm: number;
}

const UserSetting = () => {
  const {id} = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const tokenState = useRecoilValue(tokenSelector);
  const [userData, setUserData] = useRecoilState(userState);

  const setSnackBarState = useSetRecoilState(snackBarState);
  const setSnackBarToggle = useSetRecoilState(snackBarToggleState);

  const {mutate: updateMutate, isLoading} = useUpdateMutation<
    ResponseItem<EQUser>,
    Params.Update<EQUser>
  >({
    api: userApi.update,
    options: {
      onSuccess: (data: ResponseItem<EQUser>) => {
        setUserData(data?.item!);
        setSnackBarToggle(true);
        setSnackBarState("create");
        navigate(`/Home/Dashboard`);
        queryClient.invalidateQueries("OfficerList");
      },
      onError: () => {
        setSnackBarToggle(true);
        setSnackBarState("failed");
      },
    },
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: {errors},
  } = useForm<EQUser>({defaultValues: userData});

  const onSubmit: SubmitHandler<EQUser> = (data) => {
    updateMutate({
      token: tokenState,
      item: {...userData, ...data},
    });
  };

  return (
    <React.Fragment>
      <SubTitle subTitle="사용자 설정" />
      {isLoading ? (
        <MWProgress />
      ) : (
        <FormSection>
          <form onSubmit={handleSubmit(onSubmit)}>
            <MWCaption title="정보 수정" color="#546E7A" icon={<PersonIcon />} />
            <CategoryFormContainer container>
              <CategoryFormContent
                item
                xs={12}
                sm={12}
                md={6}
                xl={4}
                sx={{padding: {xs: "16px 4px", sm: "16px 8px", md: "24px"}}}
              >
                <Controller
                  name="name"
                  control={control}
                  render={({field, fieldState: {error}}) => (
                    <DocumentList>
                      <label htmlFor="name">이름 : </label>
                      <TextField
                        {...field}
                        id="name"
                        size="small"
                        placeholder="이름"
                        margin="dense"
                        autoFocus
                        sx={{width: "100%"}}
                      />
                      <Typography color="secondary">{error?.message}</Typography>
                    </DocumentList>
                  )}
                />
                <Controller
                  name="loginPW"
                  control={control}
                  render={({field, fieldState: {error}}) => (
                    <DocumentList>
                      <label htmlFor="loginPW">비밀번호 : </label>
                      <TextField
                        {...field}
                        id="loginPW"
                        type="password"
                        size="small"
                        placeholder="비밀번호"
                        margin="dense"
                        autoFocus
                        sx={{width: "100%"}}
                      />
                      <Typography color="secondary">{error?.message}</Typography>
                    </DocumentList>
                  )}
                />
                <Box sx={{width: "100%"}}>
                  <DocumentList>
                    <label htmlFor="loginPWConfirm">비밀번호 확인 : </label>
                    <TextField
                      id="loginPWConfirm"
                      type="password"
                      size="small"
                      placeholder="비밀번호 확인"
                      margin="dense"
                      autoFocus
                      sx={{width: "100%"}}
                    />
                    <Typography color="secondary">{}</Typography>
                  </DocumentList>
                </Box>
                <Controller
                  name="email"
                  control={control}
                  render={({field, fieldState: {error}}) => (
                    <DocumentList>
                      <label htmlFor="email">이메일 : </label>
                      <TextField
                        {...field}
                        id="email"
                        size="small"
                        placeholder="이메일"
                        margin="dense"
                        sx={{width: "100%", color: "#000"}}
                      />
                      <Typography color="secondary">{error?.message}</Typography>
                    </DocumentList>
                  )}
                />
                <Controller
                  name="address"
                  control={control}
                  render={({field, fieldState: {error}}) => (
                    <DocumentList>
                      <label htmlFor="address">주소 : </label>
                      <TextField
                        {...field}
                        id="address"
                        size="small"
                        placeholder="주소"
                        margin="dense"
                        disabled
                        sx={{width: "100%", color: "#000"}}
                      />
                      <Typography color="secondary">{error?.message}</Typography>
                    </DocumentList>
                  )}
                />
                <Controller
                  name="company.phone"
                  control={control}
                  render={({field, fieldState: {error}}) => (
                    <DocumentList>
                      <label htmlFor="company.phone">연락처 : </label>
                      <TextField
                        {...field}
                        id="company.phone"
                        size="small"
                        placeholder="연락처"
                        margin="dense"
                        sx={{width: "100%", color: "#000"}}
                      />
                      <Typography color="secondary">{error?.message}</Typography>
                    </DocumentList>
                  )}
                />
              </CategoryFormContent>
            </CategoryFormContainer>
            <ConfirmButtonGroup link={`/Home/Dashboard`} cancelText="취소" confirmText="수정" />
          </form>
        </FormSection>
      )}
    </React.Fragment>
  );
};

export default UserSetting;
